import type {
  Connection,
  ConnectionCreate,
  ConnectionContact,
  MatterVehicle,
  SourceOwnedConnectionCreate,
} from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import { getConnectionPreviewContact } from "./item-contact-connections";

export const VEHICLE_CONTACT_CONNECTION_TYPES = {
  "Auto Shop": "vehicle_to_auto_shop",
  Driver: "vehicle_to_driver",
  Passenger: "vehicle_to_passenger",
  Pedestrian: "vehicle_to_pedestrian",
  "Registered Owner": "vehicle_to_registered_owner",
} as const;

export const VEHICLE_PEDESTRIAN_COMPATIBILITY_CONNECTION_TYPE =
  "vehicle_to_legacy_pedestrian";

export const VEHICLE_REGISTERED_OWNER_COMPATIBILITY_CONNECTION_TYPE =
  "vehicle_to_legacy_registered_owner";

export type SupportedVehicleContactRole =
  keyof typeof VEHICLE_CONTACT_CONNECTION_TYPES;
export type VehicleContactRole = SupportedVehicleContactRole;

const isActiveVehicleContactConnection = (
  connection: Connection,
  vehicleId?: string,
) =>
  connection.sourceObjectType === "vehicle" &&
  (!vehicleId || connection.sourceObjectId === vehicleId) &&
  connection.targetObjectType === "contact" &&
  connection.state === "active";

export const getVehicleContactConnections = (
  vehicle: MatterVehicle | undefined,
  role: VehicleContactRole,
  options: { includeLegacyRegisteredOwner?: boolean } = {},
): Connection[] => {
  if (!vehicle) return [];

  const connectionTypes: string[] =
    role === "Pedestrian"
      ? [
          VEHICLE_CONTACT_CONNECTION_TYPES.Pedestrian,
          VEHICLE_PEDESTRIAN_COMPATIBILITY_CONNECTION_TYPE,
        ]
      : role === "Registered Owner" && options.includeLegacyRegisteredOwner
        ? [
            VEHICLE_CONTACT_CONNECTION_TYPES["Registered Owner"],
            VEHICLE_REGISTERED_OWNER_COMPATIBILITY_CONNECTION_TYPE,
          ]
        : [VEHICLE_CONTACT_CONNECTION_TYPES[role]];

  return vehicle.connections.filter(
    (connection) =>
      connectionTypes.includes(connection.connectionType) &&
      isActiveVehicleContactConnection(connection, vehicle.id),
  );
};

export const getVehicleContactConnection = (
  vehicle: MatterVehicle | undefined,
  role: VehicleContactRole,
): Connection | undefined => getVehicleContactConnections(vehicle, role)[0];

export const getConnectionContactName = (
  contact: ConnectionContact | null | undefined,
) => (contact ? formatContactsName(contact) : undefined) ?? "Unknown contact";

export const getVehicleConnectionFormContact = (connection?: Connection) => {
  if (!connection?.contact) return undefined;

  return {
    contactName: getConnectionContactName(connection.contact),
    id: connection.contact.id,
    type: connection.contact.type,
  };
};

export const getVehicleConnectionPreviewContact = getConnectionPreviewContact;

export const createVehicleContactConnectionInput = (
  vehicleId: string,
  contactId: string,
  role: SupportedVehicleContactRole,
): ConnectionCreate => ({
  connectionType: VEHICLE_CONTACT_CONNECTION_TYPES[role],
  operationId: crypto.randomUUID(),
  sourceObjectId: vehicleId,
  sourceObjectType: "vehicle",
  targetObjectId: contactId,
  targetObjectType: "contact",
});

export const createVehicleContactConnectionChildInput = (
  contactId: string,
  role: SupportedVehicleContactRole,
): SourceOwnedConnectionCreate => ({
  connectionType: VEHICLE_CONTACT_CONNECTION_TYPES[role],
  targetObjectId: contactId,
  targetObjectType: "contact" as const,
});

export const isVehicleContactConnection = (connection: Connection): boolean =>
  isActiveVehicleContactConnection(connection);
